import React, { useState, useEffect } from 'react';
import { FaPlusCircle, FaEdit, FaTrash, FaBolt } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import EmptyState from '../../Components/utils/EmptyContainer';
import { showToast } from '../../Components/utils/ShowToast';
import Loader from '../../Components/utils/Loader';
import BoostModal from '../../Components/seller/BoostModal';

const SellerPostings = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [b2bProfileStatus, setB2bProfileStatus] = useState('none');
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(null);
  const [showFullName, setShowFullName] = useState(null);
  /** Live boosts keyed by listing id, so each row knows its own state. */
  const [activeBoosts, setActiveBoosts] = useState({});
  const [boostTarget, setBoostTarget] = useState(null);

  const canCreateB2B = b2bProfileStatus === 'approved';

  const formatBoostDate = (d) =>
    new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });

  const daysRemaining = (endDate) =>
    Math.max(0, Math.ceil((new Date(endDate) - Date.now()) / 86400000));

  useEffect(() => {
    const fetchProducts = async () => {
      setIsLoading(true);
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };

        const productReq = axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/products/seller/product`, {
          headers,
        });

        const profileReq = axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/b2b/seller/profile`, {
          headers,
        });

        const boostReq = axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/boosts/my`, {
          headers,
        });

        const [productRes, profileRes, boostRes] = await Promise.allSettled([productReq, profileReq, boostReq]);

        if (boostRes.status === 'fulfilled' && boostRes.value.data.success) {
          setActiveBoosts(boostRes.value.data.activeByListing || {});
        }

        const marketplacePosts = productRes.status === 'fulfilled' && productRes.value.data.success
          ? (productRes.value.data.products || []).map((product) => ({
            id: product._id,
            key: `market-${product._id}`,
            isB2B: false,
            createdAt: product.createdAt,
            product: {
              name: product.title,
              image: product.thumbnail,
            },
            impressions: product.impressions || 0,
            clicks: product.clicks || 0,
            orders: product.orders || 0,
          }))
          : [];

        let approvedB2BPosts = [];
        if (profileRes.status === 'fulfilled') {
          const status = profileRes.value.data?.profile?.status || 'none';
          setB2bProfileStatus(status);

          if (status === 'approved') {
            const b2bListingRes = await axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/b2b/seller/listings`, { headers });
            approvedB2BPosts = (b2bListingRes.data?.listings || []).map((listing) => ({
              id: listing._id,
              key: `b2b-${listing._id}`,
              isB2B: true,
              createdAt: listing.createdAt,
              product: {
                name: listing.title,
                image: listing.thumbnail,
              },
              impressions: listing.impressions || 0,
              clicks: listing.clicks || 0,
              orders: listing.unitsSold || 0,
              status: listing.status,
              listingMode: listing.listingMode,
              moq: listing.moq,
            }));
          }
        } else {
          setB2bProfileStatus('none');
        }

        const merged = [...marketplacePosts, ...approvedB2BPosts].sort(
          (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
        );

        setPosts(merged);
      } catch (error) {
        console.error('Error fetching products:', error);
        showToast('Failed to fetch postings. Please try again.', 'error');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const handleDelete = async (post) => {
    try {
      setIsDeleteLoading(true);
      const endpoint = post.isB2B
        ? `${process.env.REACT_APP_Backend_Host_Name}/api/v1/b2b/seller/listings/${post.id}`
        : `${process.env.REACT_APP_Backend_Host_Name}/api/v1/products/seller/product/${post.id}`;

      const response = await axios.delete(endpoint, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.data.success) {
        setPosts((prev) => prev.filter((item) => item.key !== post.key));
        showToast(post.isB2B ? 'B2B listing deleted successfully' : 'Product deleted successfully', 'success');
      }
    } catch (error) {
      showToast('Something went wrong... Try Again!', 'error');
      console.error('Error deleting product:', error);
    } finally {
      setIsDeleteLoading(false);
      setShowConfirmation(null);
    }
  };

  const handleEdit = (post) => {
    if (post.isB2B) {
      navigate(`/createProduct/${post.id}?market=b2b`);
      return;
    }
    navigate(`/createProduct/${post.id.toString()}`);
  };

  const handleView = (post) => {
    if (post.isB2B) {
      navigate(`/seller/productDetail/${post.id}?market=b2b`);
      return;
    }
    navigate(`/seller/productDetail/${post.id}`);
  };

  const handleCreateStandard = () => {
    navigate('/createProduct/new');
  };

  const handleCreateB2B = () => {
    if (!canCreateB2B) return;
    navigate('/createProduct/new?market=b2b');
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showFullName &&
        !event.target.closest('.namePopupContent') &&
        !event.target.closest('.productNameTruncated')
      ) {
        setShowFullName(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFullName]);

  useEffect(() => {
    const handleScroll = () => {
      if (showFullName) {
        setShowFullName(null);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [showFullName]);

  if (isLoading) return <Loader type='simpleMax' />;

  return (
    <div className='sellerPostingsContainer'>
      <section className='mainSection'>
        <div className='postingsTopBar' style={{ marginTop: "15px" }}>
          <div>
            <h1 className='pageMainHeading' style={{ textAlign: "left", marginTop: "0px" }}>Postings</h1>
            <p className='postingsTopSubtitle'>
              {canCreateB2B
                ? 'Manage your marketplace and B2B listings in one place.'
                : 'Manage your marketplace listings in one place.'}
            </p>
          </div>
          <div className='postingsTopActions'>
            <button onClick={handleCreateStandard} className='modalButton submit'>
              Create New Listing
            </button>
            {canCreateB2B && (
              <button onClick={handleCreateB2B} className='modalButton submit'>
                Create B2B Listing
              </button>
            )}
          </div>
        </div>

        {posts.length === 0 ? (
          <EmptyState
            title='No Product Postings Yet'
            description={
              canCreateB2B
                ? 'Start selling by creating your first product listing. You can also create B2B listings from this page.'
                : 'Start selling by creating your first product listing.'
            }
            actionLabel='Create First Posting'
            onActionClick={handleCreateStandard}
            ActionIcon={FaPlusCircle}
          />
        ) : (
          <>
            <div className='tableContainer desktop-tablet-view'>
              <table className='postingsMainTable'>
                <thead>
                  <tr className='postingsTableHeader'>
                    <th>Product</th>
                    <th>Impressions</th>
                    <th>Clicks</th>
                    <th>Orders / Units Sold</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {posts.map((post) => (
                    <tr key={post.key} className='postingsTableRow' onClick={() => handleView(post)}>
                      <td>
                        <div className='postingsProductCell'>
                          <img
                            src={post.product.image}
                            alt={post.product.name}
                            className='postingsProductImage'
                          />
                          <div className='postingsProductMeta'>
                            <span className='postingsProductName'>{post.product.name}</span>
                            <div className='postingsBadgesRow'>
                              {post.isB2B && <span className='postingsMarketBadge'>B2B</span>}
                              {activeBoosts[post.id] && (
                                <span className='postingsBoostBadge' title={`Boosted until ${formatBoostDate(activeBoosts[post.id].endDate)}`}>
                                  <FaBolt size={10} /> Boosted · {daysRemaining(activeBoosts[post.id].endDate)}d left
                                </span>
                              )}
                              {post.isB2B && (
                                <span className='postingsB2BMetaText'>
                                  {post.listingMode} • MOQ {post.moq} • {post.status}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className='postingsMetricCell'>{post.impressions}</td>
                      <td className='postingsMetricCell'>{post.clicks}</td>
                      <td className='postingsMetricCell'>{post.orders}</td>
                      <td className='postingsActionsCell' onClick={(e) => e.stopPropagation()}>
                        <div className='postingsActionButtons'>
                          <button
                            onClick={() => setBoostTarget(post)}
                            className={`postingsBoostButton ${activeBoosts[post.id] ? 'isBoosted' : ''}`}
                            title={activeBoosts[post.id] ? 'Extend boost' : 'Boost this listing'}
                          >
                            <FaBolt size={15} />
                          </button>
                          <button
                            onClick={() => handleEdit(post)}
                            className='postingsEditButton'
                            title='Edit'
                          >
                            <FaEdit size={16} />
                          </button>
                          <button
                            onClick={() => setShowConfirmation(post)}
                            className='postingsWithdrawButton'
                            title='Delete'
                          >
                            <FaTrash size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className='mobile-view'>
              {posts.map((post) => (
                <div key={post.key} className='postingsCard' onClick={() => handleView(post)}>
                  <div className='postingsCardHeader'>
                    <div className='postingsProductCell'>
                      <img
                        src={post.product.image}
                        alt={post.product.name}
                        className='postingsProductImage'
                      />
                      <div className='productNameWrapper'>
                        <span
                          className='postingsProductName productNameTruncated'
                          onClick={() => setShowFullName(post.key)}
                        >
                          {post.product.name}
                        </span>
                        <div className='postingsBadgesRow'>
                          {post.isB2B && <span className='postingsMarketBadge'>B2B</span>}
                          {activeBoosts[post.id] && (
                            <span className='postingsBoostBadge'>
                              <FaBolt size={10} /> Boosted · {daysRemaining(activeBoosts[post.id].endDate)}d left
                            </span>
                          )}
                        </div>
                        {showFullName === post.key && (
                          <div className='namePopupContent'>
                            <p>{post.product.name}</p>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className='mobileActionsMenu' onClick={(e) => e.stopPropagation()}>
                      <div className='postingsActionButtons'>
                        <button
                          onClick={() => setBoostTarget(post)}
                          className={`postingsBoostButton ${activeBoosts[post.id] ? 'isBoosted' : ''}`}
                          title={activeBoosts[post.id] ? 'Extend boost' : 'Boost this listing'}
                        >
                          <FaBolt size={15} />
                        </button>
                        <button
                          onClick={() => handleEdit(post)}
                          className='postingsEditButton'
                          title='Edit'
                        >
                          <FaEdit size={16} />
                        </button>
                        <button
                          onClick={() => setShowConfirmation(post)}
                          className='postingsWithdrawButton'
                          title='Delete'
                        >
                          <FaTrash size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className='postingsCardStats'>
                    <div className='postingsCardStat'>
                      <span className='statLabel'>Impressions</span>
                      <span className='statValue'>{post.impressions}</span>
                    </div>
                    <div className='postingsCardStat'>
                      <span className='statLabel'>Clicks</span>
                      <span className='statValue'>{post.clicks}</span>
                    </div>
                    <div className='postingsCardStat'>
                      <span className='statLabel'>{post.isB2B ? 'Units Sold' : 'Orders'}</span>
                      <span className='statValue'>{post.orders}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        <button
          onClick={handleCreateStandard}
          className='postingsFloatingActionButton'
          title='Create posting'
        >
          <FaPlusCircle size={24} />
        </button>
      </section>

      {boostTarget && (
        <BoostModal
          post={boostTarget}
          activeBoost={activeBoosts[boostTarget.id] || null}
          onClose={() => setBoostTarget(null)}
          onBoosted={(boost) =>
            setActiveBoosts((prev) => ({
              ...prev,
              [boostTarget.id]: { endDate: boost.endDate, startDate: boost.startDate, days: boost.days },
            }))
          }
        />
      )}

      {showConfirmation && (
        <div className='modalOverlay'>
          <div className='modalContent'>
            <p>Are you sure you want to delete your posting?</p>
            <div className='modalButtons'>
              <button className='modalButton cancel' disabled={isDeleteLoading} onClick={() => setShowConfirmation(null)}>
                Close
              </button>
              <button className='modalButton submit' onClick={() => handleDelete(showConfirmation)} disabled={isDeleteLoading}>
                {isDeleteLoading ? <Loader type='miniPlus' /> : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SellerPostings;
